import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useScrollAnchor } from "@/utils/hooks/scroll-anchor.hooks";
import { useEnterSubmit } from "@/utils/hooks/enter-submit.hooks";
import { useTranslation } from "react-i18next";
import { Message, useChat } from "ai/react";
import { userSettingsAtom } from "@/atoms/user-settings";
import { useAtomValue } from "jotai/index";
import { i18nError, NotFoundError, UnauthorizedError } from "@/@types/api-error";
import { toApiError } from "@/utils/api-client";
import { useAnalytics } from "@/utils/hooks/analytics.hooks";
import { DataSchema } from "common";
import { usePrivatePageContext } from "@/utils/hooks/private-page.hooks";
import { ChatUsecase } from "@/usecases/chat/ChatUsecase";
import { useNavigate } from "react-router-dom";
import { UseFormReturn } from "react-hook-form";
import { SearchFilter, SearchFormModel, searchFormModel } from "../search-form-model";

export type ChatViewNewState = { chatId: string; viewType: "new" };
export type ChatViewLoadState = { chatId: string; viewType: "load" };
export type ChatViewCloseState = { chatId: ""; viewType: "close" };
export type ChatViewState = ChatViewNewState | ChatViewLoadState | ChatViewCloseState;

export const useChatDialog = (chatViewState: ChatViewState, form: UseFormReturn<SearchFormModel>) => {
  const { t } = useTranslation();
  const { apiClient, logout } = usePrivatePageContext();
  const { sendChatEvent } = useAnalytics();
  const { language } = useAtomValue(userSettingsAtom);
  const [apiErrorMessage, setApiErrorMessage] = useState<string>("");
  const [notFoundError, setNotFoundError] = useState<boolean>(false);
  const [apiErrorDialogOpen, setApiErrorDialogOpen] = useState<boolean>(false);
  const { scrollAnchorRef, scrollToAnchor } = useScrollAnchor();
  const [recentRallyLimit, setRecentRallyLimit] = useState<string>("");
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [chatHistoryOpen, setChatHistoryOpen] = useState(false);
  const [histories, setHistories] = useState<DataSchema.ChatMetadata[]>([]);
  const navigate = useNavigate();
  const [loadMessages, setLoadMessages] = useState<Message[]>([]);
  const [chatTitle, setChatTitle] = useState<string>("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [deleteHistory, setDeleteHistory] = useState<DataSchema.ChatMetadata>();
  const [openFilterOnChat, setOpenFilterOnChat] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState<SearchFilter>();

  const { messages, append, isLoading, input, setInput } = useChat({
    id: chatViewState.chatId,
    api: "/api/chats",
    body: {
      id: chatViewState.chatId,
      language,
      searchFilter,
    },
    onResponse: (response) => {
      if (response.status >= 400) {
        throw toApiError(response.status, response.headers.get("x-trace-id") ?? "");
      }
      setRecentRallyLimit(response.headers.get("X-Recent-Rally-Limit") ?? "");
    },
    onError: (error: Error) => {
      if (error instanceof UnauthorizedError) {
        logout();
        return;
      }

      if (error instanceof i18nError) {
        setApiErrorMessage(t(error.i18nKey));
      } else {
        setApiErrorMessage(t("api_error_network"));
      }
      setApiErrorDialogOpen(true);

      // ユーザが入力した内容を入力欄に戻す
      setInput(input);
    },
  });

  const onSubmit = useCallback(() => {
    if (isLoading) {
      return;
    }

    const content = input.trim();
    if (!content) {
      return;
    }
    sendChatEvent();

    append({ id: chatViewState.chatId, role: "user", content });

    setInput("");
  }, [append, chatViewState, input, isLoading, sendChatEvent, setInput]);

  const { onKeyDown } = useEnterSubmit(onSubmit);

  const inputPlaceholder = useMemo(() => {
    if (!recentRallyLimit || recentRallyLimit === "0") {
      return t("chat_input_placeholder");
    }
    if (messages.length <= 1 || (messages.length === 2 && isLoading)) {
      return t("chat_input_placeholder");
    }
    return t("chat_input_placeholder_second");
  }, [isLoading, messages, recentRallyLimit, t]);

  const onClickHistory = useCallback(() => {
    ChatUsecase.histories(apiClient)
      .then((response) => {
        setHistories(response.items);
        setChatHistoryOpen(true);
      })
      .catch((error) => {
        if (error instanceof UnauthorizedError) {
          logout();
          return;
        }

        if (error instanceof i18nError) {
          setApiErrorMessage(t(error.i18nKey));
        } else {
          setApiErrorMessage(t("api_error_network"));
        }
        setApiErrorDialogOpen(true);
      });
  }, [apiClient, logout, t]);

  const onClickHistoryItem = useCallback(
    (id: string) => {
      setChatHistoryOpen(false);
      navigate(`/?chatId=${id}`);
    },
    [navigate],
  );

  const callDeleteChat = useCallback(() => {
    if (!deleteHistory) {
      return;
    }
    ChatUsecase.deleteChat(apiClient, deleteHistory.id)
      .then(() => {
        const deletedHistories = histories.filter((history) => history.id !== deleteHistory.id);
        setHistories(deletedHistories);
        setDeleteHistory(undefined);
      })
      .catch((error) => {
        if (error instanceof UnauthorizedError) {
          logout();
          return;
        }

        if (error instanceof i18nError) {
          setApiErrorMessage(t(error.i18nKey));
        } else {
          setApiErrorMessage(t("api_error_network"));
        }
        setApiErrorDialogOpen(true);
      });
  }, [apiClient, deleteHistory, histories, logout, t]);

  const onClickHistoryTrash = useCallback((data: DataSchema.ChatMetadata) => {
    setDeleteHistory(data);
    setDeleteConfirmOpen(true);
  }, []);

  const fetchChat = useCallback(() => {
    ChatUsecase.chat(apiClient, chatViewState.chatId)
      .then((response) => {
        const chat = response;
        const loadMessages = chat.messages.map((chatMessage) => {
          return { ...chatMessage, id: chatViewState.chatId } as Message;
        });
        setChatTitle(chat.title);
        setLoadMessages(loadMessages);
      })
      .catch((error) => {
        if (error instanceof UnauthorizedError) {
          logout();
          return;
        }

        if (error instanceof NotFoundError) {
          setNotFoundError(true);
          return;
        }

        if (error instanceof i18nError) {
          setApiErrorMessage(t(error.i18nKey));
        } else {
          setApiErrorMessage(t("api_error_network"));
        }
        setApiErrorDialogOpen(true);
      });
  }, [apiClient, chatViewState.chatId, logout, t]);

  const onClickNew = useCallback(() => {
    navigate("/?chatId=new");
  }, [navigate]);

  const updateSearchFilter = useCallback(() => {
    const result = searchFormModel.shape.filter.safeParse(form.getValues("filter"));
    if (result.success) {
      setSearchFilter(result.data);
    } else {
      setSearchFilter(undefined);
    }
  }, [form]);

  const updateOpenFilterOnChat = useCallback(
    (open: boolean) => {
      if (!open) {
        updateSearchFilter();
      }
      setOpenFilterOnChat(open);
    },
    [updateSearchFilter],
  );

  useEffect(() => {
    scrollToAnchor();
  }, [scrollToAnchor, messages]);

  useEffect(() => {
    setRecentRallyLimit("");
    setNotFoundError(false);
    if (chatViewState.viewType === "new") {
      updateSearchFilter();
      setChatTitle(t("chat_title"));
      setInput("");
    } else if (chatViewState.viewType === "load") {
      fetchChat();
    }
  }, [chatViewState, fetchChat, setInput, t, updateSearchFilter]);

  return {
    t,
    onKeyDown,
    scrollAnchorRef,
    onSubmit,
    chatTitle,
    messages: chatViewState.viewType === "new" ? messages : loadMessages,
    isLoading,
    input,
    setInput,
    apiErrorMessage,
    apiErrorDialogOpen,
    setApiErrorDialogOpen,
    recentRallyLimit,
    inputPlaceholder,
    textAreaRef,
    chatHistoryOpen,
    setChatHistoryOpen,
    onClickHistory,
    histories,
    onClickHistoryItem,
    onClickHistoryTrash,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    deleteHistory,
    callDeleteChat,
    onClickNew,
    notFoundError,
    openFilterOnChat,
    updateOpenFilterOnChat,
  };
};
